import { mapState,mapMutations, mapActions } from "vuex";
import L from "leaflet";
import _ from "lodash"; 

/**
 * Format the API's `dt_tracker` for display.
 *
 * The server already shifts dt_tracker into the account's timezone (SQL
 * `DATE_ADD(o.dt_tracker, INTERVAL ? MINUTE)`), but it reaches us as a Date/ISO
 * string (e.g. `2026-10-10T14:42:22.000Z`). We only reformat the components —
 * passing it through `new Date()`/`toLocaleString()` would apply the timezone
 * offset a second time.
 */
export function formatTrackerTime(value) {
    if (!value) return "N/A";
    const text = String(value).trim();
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!match) return text;
    const time = match[4] + ":" + match[5] + (match[6] ? ":" + match[6] : "");
    return match[1] + "-" + match[2] + "-" + match[3] + " " + time;
}

/**
 * Live "time since last fix" label for an asset.
 *
 * The API sends `last_update.update` as an ALREADY-FORMATTED string ("Just now",
 * "5 mins ago"). That value is computed once, server-side, and then frozen:
 * `/api/objects` is a delta endpoint, so an asset that stops reporting is simply
 * omitted from later polls - the cached object keeps its old string and the
 * sidebar/home keep claiming "Just now" for a device whose last fix is hours old.
 *
 * The label therefore has to be derived from an absolute instant on the client
 * and recomputed as time passes. We prefer the raw fix instant (`dt_tracker_utc`,
 * which the API includes) because it does not depend on when we received it, and
 * fall back to the server's relative `last_update.secs` aged from the moment the
 * object was last merged.
 */
function secondsSince(asset, nowMs) {
    if (!asset) return null;

    const raw = asset.dt_tracker_utc;
    if (raw) {
        const ts = Date.parse(raw);
        if (!Number.isNaN(ts)) return Math.floor((nowMs - ts) / 1000);
    }

    const last = asset.last_update;
    if (last && typeof last.secs === "number") {
        const mergedAt = Number(asset.last_update_at) || 0;
        const elapsed = mergedAt ? Math.floor((nowMs - mergedAt) / 1000) : 0;
        return last.secs + elapsed;
    }

    return null;
}

/**
 * @param {Object} asset Asset row (needs `dt_tracker_utc` or `last_update`).
 * @param {Number} nowMs Current time; pass a reactive value so labels tick.
 * @returns {String} e.g. "Just now", "20 mins ago", "3 hrs ago", "12 days ago".
 */
export function relativeAge(asset, nowMs) {
    const now = Number(nowMs) || Date.now();
    const secs = secondsSince(asset, now);

    // Nothing usable -> echo whatever the server formatted for us.
    if (secs === null) {
        return (asset && asset.last_update && asset.last_update.update) || "";
    }

    // A fix "in the future" means clock skew (device or server); show it as
    // current rather than rendering a negative age.
    if (secs < 120) return "Just now";

    const mins = Math.floor(secs / 60);
    if (mins < 60) return mins + " mins ago";

    const hours = Math.floor(secs / 3600);
    if (hours < 24) return hours + " hrs ago";

    const days = Math.floor(secs / 86400);
    if (days < 30) return days + " days ago";

    const when = new Date(now - secs * 1000);
    return when.toLocaleDateString("en-US",
        days > 365 ? { month: "short", year: "numeric" } : { month: "short", day: "numeric" });
}

const kensrin_map = {
    
     computed: {
        ...mapState([ 
            "selected_object",
            "selected_group"
        ])
    },
    data() {
        return {
            map: null,
            zoom: 17,
            markerLayer: {},
            markersMap: {},
            frozenData:  [], // Immutable reference for change detection
            assets: [], // This will hold the currently displayed assets based on selection

            osm: L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                id: "osm",
                attribution: '&copy; OpenStreetMap contributors'
            }),

            googleStreets: L.tileLayer('http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}', {
                maxZoom: 24,
                subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
            }),

            googleHybrid: L.tileLayer('http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}', {
                maxZoom: 24,
                subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
            }),

            googleSat: L.tileLayer('http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
                maxZoom: 24,
                subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
            }),

            googleTerrain: L.tileLayer('http://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}', {
                maxZoom: 24,
                subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
            })
        };
    },

    methods: {
        ...mapMutations(["SET_NOTIFICATION"]),
        ...mapActions(["logout"]),

        initMap() {
            if (this.map) return;
            // The container only exists once a view that renders #mapid is on
            // screen (Asset Info shows an empty state when no asset is picked).
            if (!document.getElementById("mapid")) return;

            this.map = L.map("mapid", { layers: [this.osm] });  
            
            L.control.layers(
                {
                    "OpenStreetMap": this.osm,
                    "Google Streets": this.googleStreets,
                    "Google Hybrid": this.googleHybrid,
                    "Google Satellite": this.googleSat,
                    "Google Terrain": this.googleTerrain
                },
                {},
                { collapsed: true }
            ).addTo(this.map); 

            this.markerLayer = L.layerGroup().addTo(this.map);

            this.debouncedUpdate();
        },

        getMarkerHtml(vehicle) {
            vehicle.speed = parseFloat(vehicle.speed);
            if (vehicle.speed === 0 && vehicle.engineon)
                return '<span class="wlt_marker_icon IgnOn_SpdisZro"></span>';
            if (vehicle.speed === 0 && !vehicle.engineon)
                return '<span class="wlt_marker_icon IgnOff"></span>';
            if (vehicle.speed > 0 && vehicle.speed <= 60)
                return `<span class="wlt_marker_icon IgnOn_SpdnotZro" style="transform:rotate(${vehicle.angle}deg)"></span>`;
            if (vehicle.speed > 60)
                return `<span class="wlt_marker_icon Overspid" style="transform:rotate(${vehicle.angle}deg)"></span>`;
        }, 
        getPopupHtml(vehicle) {
            const speed = parseFloat(vehicle.speed) || 0;
            const engineOn = !!vehicle.engineon;
            const odometer = vehicle.odometer ? parseFloat(vehicle.odometer).toFixed(2) : '0.00';
            let brandColor = "#9E9E9E";
            // Status Logic
            let statusText = "Parked";
            let speedIcon = "mdi-car-brake-parking";
            let speedColor = "inherit";

            if (speed > 0) {
                statusText = "Moving";
                speedIcon = "mdi-car-cruise-control";
                speedColor = "green";
                brandColor = "#4CAF50"; // Green (Moving)
            } else if (engineOn) {
                statusText = "Idling";
                 brandColor = "#FF9800"; // Orange (Idling)
            } 

            return `
                <div class="custom-map-popup" style="min-width: 280px; font-family: Roboto, sans-serif; font-size: 12px; color: #333;">
                    <div style="display: flex; align-items: center; margin-bottom: 8px;">
                        <div style="flex: 0 0 40%;"><i class="mdi mdi-car" style="font-size: 24px; color: #757575;"></i></div>
                        <div style="flex: 0 0 60%; font-weight: bold; font-size: 14px;">
                            ${vehicle.plate_number || vehicle.name || 'Unknown'}
                        </div>
                    </div>

                    <div style="margin-bottom: 8px; color: #666; font-style: italic; border-bottom: 1px solid #eee; padding-bottom: 4px;">
                        ${vehicle.current_loc || 'No location data'}
                    </div>

                    <div style="display: flex; margin-bottom: 4px;">
                        <div style="flex: 0 0 40%; font-weight: bold;">Time:</div>
                        <div style="flex: 0 0 60%;">${formatTrackerTime(vehicle.dt_tracker)}</div>
                    </div>

                    <div style="display: flex; margin-bottom: 4px;">
                        <div style="flex: 0 0 40%; font-weight: bold;">Status:</div>
                        <div style="flex: 0 0 60%;">${statusText}</div>
                    </div>

                    <div style="display: flex; margin-bottom: 4px;">
                        <div style="flex: 0 0 40%; font-weight: bold;">Speed:</div>
                        <div style="flex: 0 0 60%; display: flex; align-items: center;">
                            <i class="mdi ${speedIcon}" style="font-size: 18px; margin-right: 4px; color: ${speedColor};"></i>
                            ${speed} KPH
                        </div>
                    </div>

                    ${vehicle.odometer ? `
                    <div style="display: flex; margin-bottom: 4px;">
                        <div style="flex: 0 0 40%; font-weight: bold;">Odometer:</div>
                        <div style="flex: 0 0 60%;">${odometer} km</div>
                    </div>` : ''}

                    <div style="display: flex; justify-content: center; margin-top: 13px;"> 
                        <div style="transform: rotate(${vehicle.angle || 0}deg); width: 40px; height: 40px; transition: transform 0.5s ease;">
                            <svg viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5,12 C5,6 9,2 15,2 C21,2 25,6 25,12 L25,32 C25,36 22,39 15,39 C8,39 5,36 5,32 L5,12 Z" 
                                    fill="${brandColor}" stroke="#333" stroke-width="1.5"/>
                                <path d="M8,14 C8,10 11,8 15,8 C19,8 22,10 22,14 L22,23 C22,25 20,26 15,26 C10,26 8,25 8,23 L8,14 Z" 
                                    fill="rgba(255,255,255,0.7)"/>
                                <path d="M15,4 L18,8 L12,8 Z" fill="white"/>
                                <text x="15" y="19" font-family="Arial" font-size="8" font-weight="bold" fill="white" text-anchor="middle">K</text>
                            </svg>
                        </div>
                    </div>
                </div>
            `;
        },
        updateMarkers() { 
            if (!this.map || !this.asset_groups) return;

            const selected = this.$store.state.selected_object;
            const bounds = []; 
            if (selected.length === 0) {
                this.assets = this.asset_groups.slice(0, 100);
            } else {  
                this.assets = this.asset_groups.filter(a => selected.includes(a.imei));
            }    
            
            this.assets.forEach(vehicle => { 
                // 1. Validate Coordinates first
                const lat = parseFloat(vehicle.lat);
                const lng = parseFloat(vehicle.lng);
                
                if (isNaN(lat) || isNaN(lng)) {
                    console.error(`IMEI ${vehicle.imei} has invalid coordinates:`, vehicle.lat, vehicle.lng);
                    return; 
                }

                const latlng = [lat, lng];
                
                // 2. Access the marker - Use a local variable to be safe
                let marker = this.markersMap[vehicle.imei];

                if (marker) {
                    // ✅ Update existing
                    marker.setLatLng(latlng);
                    marker.getPopup().setContent(this.getPopupHtml(vehicle));
                    
                    // Refresh Icon (Crucial if engine status changed)
                    marker.setIcon(L.divIcon({
                        className: "my-div-icon",
                        html: this.getMarkerHtml(vehicle)
                    }));
                } else { 
                    // ✅ Create new
                    const icon = L.divIcon({
                        className: "my-div-icon",
                        html: this.getMarkerHtml(vehicle)
                    });
                    
                    marker = L.marker(latlng, { icon })
                        .bindPopup(this.getPopupHtml(vehicle),{
                            maxWidth: 400,
                            className: 'vuetify-style-popup'
                        }); 

                    // ADD TO LAYER GROUP (Ensures consistency)
                    if (this.markerLayer) {
                        this.markerLayer.addLayer(marker);
                    } else {
                        marker.addTo(this.map);
                    }
                    marker.update();

                    // Use $set to ensure Vue "remembers" this marker for the next loop
                    this.$set(this.markersMap, vehicle.imei, marker);
                }   
                
                // 3. Update bounds for map centering
                if(vehicle.loc_valid == 1) {
                    bounds.push(latlng);
                }
            }); 

            // 2. Cleanup: Remove markers NOT in the current data
            Object.keys(this.markersMap).forEach(imei => {
                if (!this.assets.some(obj => obj.imei ===  imei)) {
                    this.markerLayer.removeLayer(this.markersMap[imei]);
                    delete this.markersMap[imei];
                } 
            });

            // 3. Smart Zoom
            if (bounds.length > 0) {
                // If only 1 item is selected, center on it. Otherwise, show all.
                if (bounds.length === 1) {
                    this.map.setView(bounds[0], this.zoom);
                } else {
                    this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
                }
            }

        },

        // ✅ Debounced update (prevents UI freezing)
        debouncedUpdate: _.debounce(function () {
            this.updateMarkers();
        }, 400)
    },

    watch: {
        assets: {
            handler() { 
                this.debouncedUpdate();
            },
            deep: true,
            immediate: true
        },
        "$store.state.selected_object"() {
            this.debouncedUpdate();
        },
        "$store.state.objects_loaded"() {
            this.debouncedUpdate();
        },
        asset_groups: { 
            handler() { 
                console.log('asst group changd', this.$store.state.selected_group);
                this.debouncedUpdate();
            },
            immediate: true
        }
    },

    mounted() {
        this.$nextTick(() => {
            this.initMap();
        });
    },

    beforeDestroy() {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }
};

export default kensrin_map;